import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI } from '../services/api';
import { usePageMeta } from '../services/seo';
import { useT } from '../services/i18n';

const ResetPasswordPage = () => {
  const { t } = useT();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [check, setCheck] = useState({ loading: true, email: '', error: '' });
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  usePageMeta({ title: t('auth.resetTitle'), description: t('auth.resetMeta'), noindex: true });

  useEffect(() => {
    if (!token) { setCheck({ loading: false, email: '', error: t('auth.linkIncomplete') }); return; }
    authAPI.checkResetToken(token)
      .then(r => setCheck({ loading: false, email: r.email, error: '' }))
      .catch(e => setCheck({ loading: false, email: '', error: e.response?.status === 429 ? t('auth.tooMany') : t('auth.linkInvalidDetail') }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError(t('auth.min8Long')); return; }
    if (form.password !== form.confirm) { setError(t('auth.passwordsMismatch')); return; }
    setSaving(true);
    try {
      const res = await authAPI.resetPassword(token, form.password);
      setDone(true);
      setTimeout(() => navigate(res.user?.is_admin ? '/admin' : '/mon-compte'), 1800);
    } catch (err) {
      setError(err.response?.status === 400 || err.response?.status === 404 ? t('auth.linkInvalidDetail') : t('auth.cannotChange'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block"><Logo variant="light" size="lg" className="mx-auto" /></Link>
          <h1 className="mt-4 text-2xl font-bold text-gray-900" data-testid="reset-title">{t('auth.resetTitle')}</h1>
          {check.email && <p className="mt-2 text-sm text-gray-600">{t('auth.account')} <strong data-testid="reset-email">{check.email}</strong></p>}
        </div>
        <div className="bg-white rounded-lg shadow-md p-8" data-testid="reset-password-page">
          {check.loading ? (
            <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-500" /></div>
          ) : check.error ? (
            <div className="text-center" data-testid="reset-token-invalid">
              <div className="w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4"><AlertCircle size={30} /></div>
              <p className="text-gray-800 font-medium">{t('auth.linkInvalid')}</p>
              <p className="text-sm text-gray-600 mt-2">{check.error}</p>
              <Link to="/mot-de-passe-oublie" className="inline-block mt-6 text-sm text-blue-600 hover:underline" data-testid="reset-request-again">{t('auth.requestNew')}</Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center mx-auto mb-4"><CheckCircle size={30} /></div>
              <p className="text-gray-800 font-medium">{t('auth.changed')}</p>
              <p className="text-sm text-gray-600 mt-2">{t('auth.redirecting')}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm" data-testid="reset-error">{error}</div>}
              {[['password', t('auth.newPassword'), 'reset-password-input'], ['confirm', t('auth.confirmPassword'), 'reset-confirm-input']].map(([key, label, tid]) => (
                <div key={key}>
                  <Label htmlFor={key}>{label}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input id={key} type={show ? 'text' : 'password'} data-testid={tid} value={form[key]} onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))} className="pl-10 pr-10" placeholder={t('auth.min8Ph')} />
                    {key === 'password' && (
                      <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="reset-toggle-visibility">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                    )}
                  </div>
                </div>
              ))}
              <Button type="submit" disabled={saving} data-testid="reset-submit" className="w-full bg-orange-500 hover:bg-orange-600">
                {saving && <Loader2 size={16} className="mr-2 animate-spin" />} {t('auth.saveNew')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
